// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Several logistics plans for one job.
 *
 * A site is not a moment. The plan for demolition is not the plan for the
 * crane set, and a single drawing leaves everyone to imagine the rest.
 *
 * Each plan is a free canvas — no rules about what belongs on which, no link
 * to the estimate's phases, no per-element lifespans. You draw what you want,
 * duplicate it, change what moved, and name it whatever the job calls that
 * stretch of work.
 *
 * Every plan is equal. An earlier version kept the first one in the module's
 * top-level fields and the rest in a list, which meant the first could not be
 * reordered or removed — a seam that would show the moment anyone dragged a
 * tab. The list is now authoritative and holds all of them; the top-level
 * fields are kept as a mirror of whichever plan is first, so a module saved
 * before any of this still opens and the sandbox builder still works.
 */

import type { SiteLogistics, SitePlan } from "./logistics";

export const FIRST_PLAN_ID = "plan_1";

/** The drawing fields, without the plan list wrapped around them. */
function canvasOf(src: SiteLogistics | SitePlan): Omit<SitePlan, "id" | "name"> {
  return {
    imageDataUrl: src.imageDataUrl ?? null,
    icons: src.icons || [],
    lines: src.lines || [],
    areas: src.areas || [],
    circles: src.circles || [],
    texts: src.texts || [],
    dimensions: src.dimensions || [],
    ...(src.blocks ? { blocks: src.blocks } : {}),
    notes: src.notes || "",
    scale: src.scale || { preset: "none", feetPerPx: null },
  };
}

/**
 * Every plan on the job, in order.
 *
 * A module saved before plans existed has no list, so the drawing already
 * there becomes the one plan. Nothing is converted on disk until something is
 * actually changed.
 */
export function plansOf(sl: SiteLogistics | null | undefined): SitePlan[] {
  if (!sl) return [];
  if (sl.plans && sl.plans.length) return sl.plans;
  return [{ id: FIRST_PLAN_ID, name: sl.firstPlanName || "Plan 1", ...canvasOf(sl) }];
}

/**
 * Writes the whole list back, mirroring the first plan into the top-level
 * fields so anything still reading those sees a real drawing rather than an
 * empty one.
 */
export function writePlans(sl: SiteLogistics, plans: SitePlan[], activePlanId?: string): SiteLogistics {
  if (!plans.length) return sl;               // a job always has at least one plan
  const active = plans.some((p) => p.id === activePlanId) ? activePlanId : plans[0].id;
  return {
    ...sl,
    ...canvasOf(plans[0]),
    firstPlanName: plans[0].name,
    plans,
    activePlanId: active,
  };
}

/** The plan being edited. Falls back to the first rather than to nothing. */
export function activePlan(sl: SiteLogistics): SitePlan {
  const all = plansOf(sl);
  return all.find((p) => p.id === sl.activePlanId) || all[0];
}

/** Replaces one plan's contents. */
export function withPlan(sl: SiteLogistics, planId: string, next: SitePlan): SiteLogistics {
  const all = plansOf(sl).map((p) => (p.id === planId ? next : p));
  return writePlans(sl, all, sl.activePlanId);
}

/**
 * Changes whichever plan is open.
 *
 * The module keeps the first plan's drawing mirrored in its own top-level
 * fields, for the sake of anything written before plans existed. That mirror
 * is a copy, not the plan: writing to it changes nothing anyone can see,
 * because every reader goes through activePlan(). Code outside this tab --
 * the keyboard shortcuts, in particular -- was doing exactly that, so Delete
 * and the arrow keys silently did nothing as soon as a job had a plan list.
 */
export function updateActivePlan(
  sl: SiteLogistics, fn: (plan: SitePlan) => SitePlan,
): SiteLogistics {
  const plan = activePlan(sl);
  if (!plan) return sl;
  return withPlan(sl, plan.id, fn(plan));
}

/** A name that does not collide, for a duplicate. */
export function copyName(existing: string[], base: string): string {
  const taken = new Set(existing);
  const stem = base.replace(/ \(copy( \d+)?\)$/, "");
  let candidate = `${stem} (copy)`;
  let n = 2;
  while (taken.has(candidate)) candidate = `${stem} (copy ${n++})`;
  return candidate;
}

/** Duplicates a plan, placing the copy straight after it. */
export function duplicatePlan(sl: SiteLogistics, planId: string, newId: string): SiteLogistics {
  const all = plansOf(sl);
  const at = all.findIndex((p) => p.id === planId);
  if (at < 0) return sl;
  const src = all[at];

  // Deep enough that editing the copy cannot reach the original, which is the
  // whole point of duplicating.
  const copy: SitePlan = {
    id: newId,
    name: copyName(all.map((p) => p.name), src.name),
    imageDataUrl: src.imageDataUrl,
    icons: src.icons.map((e) => ({ ...e })),
    lines: src.lines.map((e) => ({ ...e, points: e.points.map((pt) => ({ ...pt })) })),
    areas: src.areas.map((e) => ({ ...e, points: e.points.map((pt) => ({ ...pt })) })),
    circles: src.circles.map((e) => ({ ...e })),
    texts: (src.texts || []).map((e) => ({ ...e })),
    dimensions: (src.dimensions || []).map((e) => ({ ...e, a: { ...e.a }, b: { ...e.b } })),
    // Where the blocks were put carries to the copy: the next phase of the
    // same site wants its legend in the same corner.
    ...(src.blocks ? { blocks: JSON.parse(JSON.stringify(src.blocks)) } : {}),
    notes: src.notes,
    scale: { ...src.scale },
    ...(src.phase ? { phase: { ...src.phase } } : {}),
    // The sheet number is deliberately NOT carried: two sheets in one set
    // cannot both be SL-1, and the blank falls back to position.
    ...(src.issueDate ? { issueDate: src.issueDate } : {}),
    // Revisions belong to the sheet that was issued, not to a copy of it
    // that has never been out of the office.
  };

  return writePlans(sl, [...all.slice(0, at + 1), copy, ...all.slice(at + 1)], newId);
}

/** Adds an empty plan at the end and selects it. */
export function addPlan(sl: SiteLogistics, newId: string, name?: string): SiteLogistics {
  const all = plansOf(sl);
  const blank: SitePlan = {
    id: newId,
    name: name || `Plan ${all.length + 1}`,
    // The site plan image and scale carry over: it is the same site, and
    // re-uploading the drawing for every plan would be the first complaint.
    imageDataUrl: all[0]?.imageDataUrl ?? null,
    icons: [], lines: [], areas: [], circles: [], texts: [], dimensions: [],
    notes: "",
    scale: { ...(all[0]?.scale || { preset: "none", feetPerPx: null }) },
  };
  return writePlans(sl, [...all, blank], newId);
}

/**
 * Sets when a plan is in effect.
 *
 * An empty start or end clears that end rather than storing a blank, so a
 * half-typed date in a field cannot become a date nobody meant.
 */
export function setPhase(sl: SiteLogistics, planId: string,
  phase: { start?: string; end?: string }): SiteLogistics {
  const clean = {
    ...(phase.start ? { start: phase.start } : {}),
    ...(phase.end ? { end: phase.end } : {}),
  };
  return writePlans(sl, plansOf(sl).map((p) => (p.id === planId
    ? (Object.keys(clean).length ? { ...p, phase: clean } : (({ phase: _d, ...rest }) => rest)(p))
    : p)), sl.activePlanId);
}

export function renamePlan(sl: SiteLogistics, planId: string, name: string): SiteLogistics {
  return writePlans(sl, plansOf(sl).map((p) => (p.id === planId ? { ...p, name } : p)), sl.activePlanId);
}

/**
 * Removes a plan.
 *
 * The last one is kept: a logistics module with no plan has nowhere to draw,
 * and "Clear All" already empties a plan without that problem.
 */
export function removePlan(sl: SiteLogistics, planId: string): SiteLogistics {
  const all = plansOf(sl);
  if (all.length <= 1) return sl;
  const rest = all.filter((p) => p.id !== planId);
  const active = sl.activePlanId === planId ? rest[0].id : sl.activePlanId;
  return writePlans(sl, rest, active);
}

/**
 * Moves a plan to a new position, the way a browser tab moves.
 *
 * Indices are positions in the list as the user sees it, so a caller can hand
 * over what it got from a drag without converting anything.
 */
export function reorderPlans(sl: SiteLogistics, from: number, to: number): SiteLogistics {
  const all = plansOf(sl);
  if (from === to) return sl;
  if (from < 0 || from >= all.length) return sl;
  const next = [...all];
  const [moved] = next.splice(from, 1);
  // Clamped rather than rejected: a drag past the last tab means "put it last",
  // which is what the pointer was saying.
  next.splice(Math.max(0, Math.min(to, next.length)), 0, moved);
  return writePlans(sl, next, sl.activePlanId);
}
